import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Settings } from "../../../settings";

/**
 * Controls tension spine animations for individual reels
 */
class TensionReelController {
  constructor(reelContainer, reelIndex, symbolWidth, symbolHeight) {
    this.reelContainer = reelContainer;
    this.tensionSpineData = {
      skeleton: "tension-skeleton",
      atlas: "tension-atlas",
    };
    this.reelIndex = reelIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    this.tensionSpine = null;
    this.isVisible = false;
    this.initialized = false;

    // Spine scaling configuration
    this.heightMultiplier = 1.0; // Default size, no scaling
    this.yOffsetMultiplier = -0.03; // No offset

    // Track active timeouts for cleanup
    this.currentTimeouts = [];

    // Internal caches for performance optimization
    this._spineScaleCache = null;
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;
    this._availableAnimationsCache = null;
    this._tensionOutDurationCache = null;
    this._tensionCycleDurationCache = null;

    this.initializeTensionSpine();
  }

  /**
   * Calculate responsive scale and positioning for spine animation
   * @param {Spine} spineInstance - The spine instance to scale
   * @param {number} containerWidth - Available container width
   * @param {number} containerHeight - Available container height
   * @returns {Object} - Scale and positioning information
   * Cached for performance - invalidated when dimensions change
   */
  calculateSpineScale(spineInstance, containerWidth, containerHeight) {
    // Check cache first
    const cacheKey = `${containerWidth}_${containerHeight}`;
    if (
      this._spineScaleCache &&
      this._spineScaleCache.cacheKey === cacheKey &&
      this._lastSymbolWidth === containerWidth &&
      this._lastSymbolHeight === containerHeight
    ) {
      return this._spineScaleCache.result;
    }

    // Set default animation to get proper bounds
    if (
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.setAnimation(0, defaultAnimation.name, true);
    }

    // Calculate scale to fit within container while maintaining aspect ratio
    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;

    const widthScale = containerWidth / nativeWidth;
    const heightScale = containerHeight / nativeHeight;
    const scale = Math.min(widthScale, heightScale);

    // Calculate final dimensions and centering offsets
    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;

    const result = {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };

    // Cache result
    this._spineScaleCache = {
      cacheKey,
      result,
    };
    this._lastSymbolWidth = containerWidth;
    this._lastSymbolHeight = containerHeight;

    return result;
  }

  /**
   * Hide tension animation immediately
   */
  hideTension() {
    if (!this.tensionSpine || this.tensionSpine.destroyed) {
      this.isVisible = false;
      return;
    }

    requestAnimationFrame(() => {
      if (!this.tensionSpine || this.tensionSpine.destroyed) {
        this.isVisible = false;
        return;
      }

      this.tensionSpine.alpha = 0.001;
      this.tensionSpine.autoUpdate = false;
      this.isVisible = false;

      if (this.tensionSpine.state) {
        try {
          this.tensionSpine.state.clearTracks();
        } catch (error) {
          // Clear tracks error
        }
      }
      if (this.tensionSpine.skeleton?.setToSetupPose) {
        this.tensionSpine.skeleton.setToSetupPose();
      }
    });
  }

  /**
   * Force stop tension animations with smooth exit transition
   */
  forceStopTension() {
    this.clearAllTimeouts();

    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state ||
      !this.getAvailableAnimations().includes("tension_out")
    ) {
      this.hideTension();
      return;
    }

    // Check if currently playing tension animations
    const currentAnimation = this.tensionSpine.state.getCurrent(0);

    // If tension_out is already playing, don't restart it
    if (currentAnimation && currentAnimation.animation.name === "tension_out") {
      return; // Already transitioning out, let it finish
    }

    const isPlayingTension =
      currentAnimation &&
      (currentAnimation.animation.name === "tension_in" ||
        currentAnimation.animation.name === "tension_idle");

    if (isPlayingTension) {
      // Switch to tension_out and play to completion
      this.tensionSpine.state.setAnimation(0, "tension_out", false);

      // Schedule hide based on animation duration
      const animationDuration =
        this.tensionSpine.skeleton.data.animations.find(
          (anim) => anim.name === "tension_out"
        )?.duration || 1.0;

      const timeoutId = setTimeout(() => {
        this.hideTension();
      }, animationDuration * 1000);

      this.currentTimeouts.push(timeoutId);
    } else {
      this.hideTension();
    }
  }

  /**
   * Clear all active timeouts
   */
  clearAllTimeouts() {
    this.currentTimeouts.forEach(clearTimeout);
    this.currentTimeouts = [];
  }

  /**
   * Check if tension is currently visible and playing
   * Returns true if tension is visible and should not be restarted
   * @returns {boolean} True if tension is visible and active
   */
  isTensionVisible() {
    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state
    ) {
      return false;
    }

    // Check if spine is visible (alpha > 0.5 means it's showing)
    if (this.tensionSpine.alpha <= 0.5) {
      return false;
    }

    // Check if currently playing tension animations
    const currentAnimation = this.tensionSpine.state.getCurrent(0);
    if (!currentAnimation) {
      return false;
    }

    const animationName = currentAnimation.animation.name;
    const isVisible = (
      animationName === "tension_in" ||
      animationName === "tension_idle" ||
      animationName === "tension_out"  // Include tension_out - it's still visible!
    );

    return isVisible;
  }

  /**
   * Play tension intro animation and transition to idle loop
   */
  playTensionIn() {
    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state
    ) {
      return;
    }

    try {
      // Check if warmup already has tension_in loaded and paused — just unpause
      const currentAnim = this.tensionSpine.state.getCurrent(0);
      const isWarmupState =
        currentAnim &&
        currentAnim.animation.name === "tension_in" &&
        this.tensionSpine.state.timeScale === 0;

      if (isWarmupState) {
        // Fast path: warmup already set up tension_in, just unpause and show
        this.tensionSpine.alpha = 1;
        this.tensionSpine.autoUpdate = true;
        this.tensionSpine.state.timeScale = 1;
        this.isVisible = true;

        // Queue idle loop after tension_in finishes
        const animations = this.getAvailableAnimations();
        if (animations.includes("tension_idle")) {
          this.tensionSpine.state.addAnimation(0, "tension_idle", true, 0);
        }
      } else {
        // Full setup path: reset and start fresh
        this.tensionSpine.state.clearTracks();
        this.tensionSpine.state.clearListeners();
        this.tensionSpine.skeleton.setToSetupPose();
        this.tensionSpine.state.apply(this.tensionSpine.skeleton);

        this.tensionSpine.alpha = 1;
        this.tensionSpine.autoUpdate = true;
        this.tensionSpine.state.timeScale = 1;
        this.isVisible = true;

        const animations = this.getAvailableAnimations();

        if (
          animations.includes("tension_in") &&
          animations.includes("tension_idle")
        ) {
          this.tensionSpine.state.setAnimation(0, "tension_in", false);
          this.tensionSpine.state.addAnimation(0, "tension_idle", true, 0);
        } else if (animations.includes("tension_idle")) {
          this.tensionSpine.state.setAnimation(0, "tension_idle", true);
        } else if (animations.length > 0) {
          this.tensionSpine.state.setAnimation(0, animations[0], true);
        }
      }
    } catch (error) {
      this.hideTension();
    }
  }

  /**
   * Play tension exit animation
   */
  playTensionOut() {
    if (
      !this.tensionSpine ||
      this.tensionSpine.destroyed ||
      !this.tensionSpine.state
    ) {
      return;
    }

    // Check if tension_out is already playing - don't restart it
    const currentAnimation = this.tensionSpine.state.getCurrent(0);
    if (currentAnimation && currentAnimation.animation.name === "tension_out") {
      return; // Already playing tension_out, let it finish
    }

    this.clearAllTimeouts();

    try {
      const animations = this.getAvailableAnimations();

      let animationToPlay = null;

      if (animations.includes("tension_out")) {
        animationToPlay = "tension_out";
      } else if (animations.length > 0) {
        animationToPlay = animations[0];
      }

      if (animationToPlay) {
        // Seamless transition: don't clearTracks or setToSetupPose
        // Just set the animation directly — spine transitions from idle → out without a flash
        this.tensionSpine.state.setAnimation(0, animationToPlay, false);

        // Get actual animation duration from spine
        const animationDuration =
          this.tensionSpine.skeleton.data.animations.find(
            (anim) => anim.name === animationToPlay
          )?.duration || 1.0;

        const timeoutId = setTimeout(() => {
          this.hideTension();
        }, animationDuration * 1000);
        this.currentTimeouts.push(timeoutId);
      } else {
        this.hideTension();
      }
    } catch (error) {
      this.hideTension();
    }
  }

  /**
   * Update tension spine dimensions for responsive scaling
   * @param {number} newsymbolWidth - New symbol width
   * @param {number} newsymbolHeight - New symbol height
   */
  resizeTension(newsymbolWidth, newsymbolHeight) {
    if (
      newsymbolWidth === this.symbolWidth &&
      newsymbolHeight === this.symbolHeight
    ) {
      return;
    }

    this.symbolWidth = newsymbolWidth;
    this.symbolHeight = newsymbolHeight;

    // Invalidate caches when dimensions change
    this._spineScaleCache = null;
    this._lastSymbolWidth = newsymbolWidth;
    this._lastSymbolHeight = newsymbolHeight;

    if (!this.tensionSpine || this.tensionSpine.destroyed) {
      return;
    }

    requestAnimationFrame(() => {
      if (!this.tensionSpine || this.tensionSpine.destroyed) {
        return;
      }

      try {
        this.applyTensionScaling();
      } catch (error) {
        // Fail silently
      }
    });
  }

  /**
   * Apply scaling and positioning to tension spine
   */
  applyTensionScaling() {
    if (!this.tensionSpine || this.tensionSpine.destroyed) {
      return;
    }

    const scaleData = this.calculateSpineScale(
      this.tensionSpine,
      this.symbolWidth,
      this.symbolHeight
    );

    if (this.tensionSpine.scale) {
      this.tensionSpine.scale.set(
        scaleData.scale,
        scaleData.scale * this.heightMultiplier
      );
    }

    if (this.tensionSpine.position) {
      const yOffset = this.symbolHeight * this.yOffsetMultiplier;
      this.tensionSpine.position.set(scaleData.x, scaleData.y + yOffset);
    }
  }

  /**
   * Create tension spine instance
   */
  createTensionSpine() {
    try {
      if (!this.tensionSpineData) {
        return null;
      }

      const assets = PIXI.Assets.cache;
      if (
        !assets.has(this.tensionSpineData.skeleton) ||
        !assets.has(this.tensionSpineData.atlas)
      ) {
        return null;
      }

      const skeletonData = PIXI.Assets.get(this.tensionSpineData.skeleton);
      const atlasData = PIXI.Assets.get(this.tensionSpineData.atlas);

      if (!skeletonData || !atlasData) {
        return null;
      }

      const tensionSpine = Spine.from({
        skeleton: this.tensionSpineData.skeleton,
        atlas: this.tensionSpineData.atlas,
      });

      if (!tensionSpine || !tensionSpine.skeleton || !tensionSpine.state) {
        return null;
      }

      if (
        !tensionSpine.skeleton.data ||
        !tensionSpine.skeleton.data.animations
      ) {
        return null;
      }

      return tensionSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get available animation names
   * Cached for performance - invalidated when spine is reinitialized
   */
  getAvailableAnimations() {
    if (!this.tensionSpine?.skeleton?.data?.animations) {
      return [];
    }

    // Check cache first
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    const animations = this.tensionSpine.skeleton.data.animations.map(
      (anim) => anim.name
    );

    // Cache result
    this._availableAnimationsCache = animations;

    return animations;
  }

  /**
   * Get tension_out animation duration in milliseconds
   * @returns {number} Duration in milliseconds
   * Cached for performance - invalidated when spine is reinitialized
   */
  getTensionOutDuration() {
    if (!this.tensionSpine?.skeleton?.data?.animations) {
      return 1000; // Fallback to 1 second
    }

    // Check cache first
    if (this._tensionOutDurationCache !== null) {
      return this._tensionOutDurationCache;
    }

    const tensionOutAnim = this.tensionSpine.skeleton.data.animations.find(
      (anim) => anim.name === "tension_out"
    );

    const duration = tensionOutAnim ? tensionOutAnim.duration * 1000 : 1000;

    // Cache result
    this._tensionOutDurationCache = duration;

    return duration;
  }

  /**
   * Get full tension cycle duration (tension_in + tension_idle + tension_out) in milliseconds.
   * Used by ReelControlContext to time how long each tension reel spins.
   * Reads actual spine animation durations for precision — same pattern as useWinController.
   * @returns {number} Duration in milliseconds
   * Cached for performance — invalidated when spine is reinitialized
   */
  getTensionCycleDuration() {
    if (!this.tensionSpine?.skeleton?.data?.animations) {
      return null; // Return null so caller can fall back to config
    }

    // Check cache first
    if (this._tensionCycleDurationCache !== null) {
      return this._tensionCycleDurationCache;
    }

    const animations = this.tensionSpine.skeleton.data.animations;

    const inDuration = (animations.find((a) => a.name === "tension_in")?.duration || 0) * 1000;
    const idleDuration = (animations.find((a) => a.name === "tension_idle")?.duration || 0) * 1000;
    const outDuration = (animations.find((a) => a.name === "tension_out")?.duration || 0) * 1000;

    const cycleDuration = inDuration + idleDuration + outDuration;

    // Only cache if we got meaningful data
    if (cycleDuration > 0) {
      this._tensionCycleDurationCache = cycleDuration;
      return cycleDuration;
    }

    return null; // No valid animations found, let caller use fallback
  }

  /**
   * Warm up tension spine with "tension_in" animation
   */
  warmUpTensionSpine() {
    if (!this.tensionSpine || !this.tensionSpine.state) {
      return;
    }

    try {
      const animations = this.getAvailableAnimations();

      if (animations.includes("tension_in")) {
        // Set animation to trigger shader compilation and mesh creation
        this.tensionSpine.state.setAnimation(0, "tension_in", false);
        this.tensionSpine.state.update(0.016); // Advance one frame
        this.tensionSpine.skeleton.updateWorldTransform(); // Populate bone/slot hierarchy for GPU

        // Pause at frame 0 — GPU has already compiled everything
        // Do NOT clearTracks here — keeping the animation preserves spine bounds
        this.tensionSpine.state.timeScale = 0;
      }

      // Hidden but in render pipeline, autoUpdate off since paused
      this.tensionSpine.alpha = 0.001;
      this.tensionSpine.autoUpdate = false;
    } catch (error) {
      // Silent fail - warmup is optional optimization
    }
  }

  /**
   * Initialize tension spine and add to container
   */
  initializeTensionSpine() {
    this.tensionSpine = this.createTensionSpine();

    if (!this.tensionSpine) {
      return;
    }

    // Validate spine components
    const missingComponents = [];
    if (!this.tensionSpine.skeleton) missingComponents.push("skeleton");
    if (!this.tensionSpine.state) missingComponents.push("state");
    if (!this.tensionSpine.skeleton?.data)
      missingComponents.push("skeleton.data");
    if (!this.tensionSpine.skeleton?.slots)
      missingComponents.push("skeleton.slots");
    if (!this.tensionSpine.skeleton?.bones)
      missingComponents.push("skeleton.bones");

    if (missingComponents.length > 0) {
      return;
    }

    this.tensionSpine.position.set(0, 0);
    this.tensionSpine.zIndex = 5;
    this.tensionSpine.alpha = 0.001; // Hidden initially, keep in render pipeline
    this.isVisible = false;

    this.reelContainer.addChild(this.tensionSpine);
    this.applyTensionScaling();

    if (this.tensionSpine.parent !== this.reelContainer) {
      return;
    }

    // Invalidate animation caches when spine is initialized
    this._availableAnimationsCache = null;
    this._tensionOutDurationCache = null;
    this._tensionCycleDurationCache = null;

    // Minimal warmup - warm up "tension_in" since that's what plays first
    this.warmUpTensionSpine();

    this.initialized = true;
  }

  /**
   * Clean up spine resources and references
   */
  cleanup() {
    this.clearAllTimeouts();

    if (this.tensionSpine && !this.tensionSpine.destroyed) {
      try {
        if (this.tensionSpine.state) {
          this.tensionSpine.state.clearTracks();
          this.tensionSpine.state.clearListeners();
        }

        if (this.tensionSpine.parent) {
          this.tensionSpine.parent.removeChild(this.tensionSpine);
        }

        this.tensionSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Fail silently
      }
    }

    this.tensionSpine = null;
    this.reelContainer = null;
    this.tensionSpineData = null;
    this.isVisible = false;
    this.initialized = false;
  }
}

export default TensionReelController;
